import { useState, useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { useHistory } from 'react-router';
import moment from "moment";

import { Button, Typography, Divider, Table, Image, Skeleton } from "antd";
import { PrinterOutlined, WarningOutlined } from '@ant-design/icons';

import notifier from 'utils/notifier';
import { ErrorResponse } from 'utils/types';
import { toLetters, getDisplayCompanyId } from 'utils/helpers';

import { getEZ } from 'store/claim/actions'
import { getAttachments } from 'store/claim/member/actions';
import { reportIssue } from 'store/app/actions';

import ReportIssueModal from 'components/Claims/ReportIssueModal';

import astranaLogoPlum  from 'assets/images/Astrana_Logo_Plum.svg';
import { SearchClaimDetailProps } from './Proptypes';
import "./index.scss";

const { Title } = Typography;

const SearchClaimDetail = (props: SearchClaimDetailProps) => {
  const history = useHistory();
  const [claimDetailMaster, setClaimDetailMaster] = useState<any>({});
  const [diagDataSource, setDiagDataSource] = useState([]);
  const [detailsDataSource, setDetailsDataSource] = useState([]);
  const [attachmentList, setAttachmentList] = useState<string[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);


  useEffect(() => {
    window.addEventListener("popstate", backEvent);
    return () => {
      window.removeEventListener("popstate", backEvent);
    }
  }, []);

  useEffect(() => {
    getEZClaimData();
    getClaimAttachments();
  }, []);

  const summary = {
    key: "summary",
    "RmiClaimNo": "summary",
    "RmiModif": "",
    "RmiModif2": "",
    "RmiModif3": "",
    "RmiModif4": "",
    "RmiProcCode": "",
    "RmiDiagRefNum": "summary",
    "RmiQty": "summary",
    "RmiDiagCode": "summary",
    "RmiCopay": "summary",
    "RmiDeductible": "summary",
    "RmiDeductibleL2": "summary",
    "RmiBilled": "summary",
    "RmiAdjCode": "",
    "RmiNet": "summary",
    "RmiFromDateSvc": "summary",
    "RmiToDateSvc": "summary",
    "RmiAdjust": "",
}

  const diagColumns = [
    {
      title: 'REFERENCE',
      key: 'RmiDiagRefNo',
      dataIndex: 'RmiDiagRefNo',
      render: (refNo: number) => toLetters(refNo)
    },
    {
      title: 'DIAG CODE',
      key: 'RmiDiag',
      dataIndex: 'RmiDiag',
    },
    {
      title: 'DESCRIPTION',
      key: 'RmiDiagDesc',
      dataIndex: 'RmiDiagDesc',
    },
  ];

  const procColumns = [
    {
      title: <>FROM<br />TO</>,
      key: 'RmiFromDateSvc',
      dataIndex: 'RmiFromDateSvc',
      render: (text: string, record: any) => {
        if (record.key === 'summary') return;
        return (
            <>
            {moment(text).format('MM/DD/YYYY')}<br />{moment(record.RmiToDateSvc).format('MM/DD/YYYY')}<br/><span>{record.RmiTotalTime ? `${record.RmiTotalTime} minutes` : ''}</span>
            </>
          );
      }
    },
    {
      title: 'CPT',
      key: 'RmiProcCode',
      dataIndex: 'RmiProcCode',
    },
    {
      title: 'MODIFIER',
      key: 'RmiModif',
      dataIndex: 'RmiModif',
      render: (text: string, record: any) => <>{text} {record.RmiCptModifier2} {record.RmiCptModifier3} {record.RmiCptModifier4}</>
    },
    {
      title: 'DIAG REF',
      key: 'RmiDiagRefNum',
      dataIndex: 'RmiDiagRefNum',
      render: (text: string, record: any) => {
        if (record.key === 'summary') {
          return;
        }

        return (
          <>
            {text ? toLetters(parseInt(text)) : null}&nbsp;
            {record.RmiDiagPtr2 ? toLetters(parseInt(record.RmiDiagPtr2)) : null}&nbsp;
            {record.RmiDiagPtr3 ? toLetters(parseInt(record.RmiDiagPtr3)) : null}&nbsp;
            {record.RmiDiagPtr4 ? toLetters(parseInt(record.RmiDiagPtr4)) : null}
          </>
        )
      }
    },
    {
      title: 'QTY',
      key: 'RmiQty',
      dataIndex: 'RmiQty',
      render: (text: string) => {
        if (text === 'summary') {
         return (
            <div style={{ fontWeight: 'bolder' }}>
              Totals
            </div>
          );
        } else {
          return text;
        }
      }
    },
    {
      title: 'BILLED',
      key: 'RmiBilled',
      dataIndex: 'RmiBilled',
      render: (text: string) => {
        if (text === 'summary') {
          let billTotal = detailsDataSource.reduce((accumulator: any, item: any) => {
            return accumulator + parseFloat(item.RmiBilled);
          }, 0);

          return (
            <div style={{ fontWeight: 'bolder' }}>
              Bill Total:
              <br />
              ${billTotal.toFixed(2)}
            </div>
          );
        } else {
          return `$${parseFloat(text).toFixed(2)}`;
        }
      }
    },
    {
      title: <>CO-PAY<br />CO-INSURE</>,
      key: 'RmiCopay',
      dataIndex: 'RmiCopay',
      render: (text: string) => {
        if (text === 'summary') {
          let copayTotal = detailsDataSource.reduce((accumulator: any, item: any) => {
            return accumulator + parseFloat(item.RmiCopay);
          }, 0);

          return (
            <div style={{ fontWeight: 'bolder' }}>
              Copay Total:
              <br />
              ${copayTotal.toFixed(2)}
            </div>
          );
        } else {
          return `$${parseFloat(text).toFixed(2)}`;
        }
      }
    },
    {
      title: 'DED',
      key: 'RmiDeductible',
      dataIndex: 'RmiDeductible',
      render: (text: string) => {
        if (text === 'summary') {
          let dedTotal = detailsDataSource.reduce((accumulator: any, item: any) => {
            return accumulator + parseFloat(item.RmiDeductible);
          }, 0);

          return (
            <div style={{ fontWeight: 'bolder' }}>
              Deductible Total:
              <br />
              ${dedTotal.toFixed(2)}
            </div>
          );
        } else {
          return `$${parseFloat(text).toFixed(2)}`;
        }
      }
    },
    {
      title: 'ADJ',
      key: 'RmiAdjCode',
      dataIndex: 'RmiAdjCode',
    },
    {
      title: 'NET PAID',
      key: 'RmiNet',
      dataIndex: 'RmiNet',
      render: (text: string) => {
        if (text === 'summary') {
          let netTotal = detailsDataSource.reduce((accumulator: any, item: any) => {
            return accumulator + parseFloat(item.RmiNet);
          }, 0);

          return (
            <div style={{ fontWeight: 'bolder' }}>
              Net Total:
              <br />
              ${netTotal.toFixed(2)}
            </div>
          );
        } else {
          return `$${parseFloat(text).toFixed(2)}`;
        }
      }
    },
  ];

  const backEvent = (e: any) => {
    if (e.state === null) {
      e.preventDefault();
      return false;
    }

    history.push('/claims/search');
    return props.setPage('start');
  }

  const getEZClaimData = () => {
    return props.getEZ(props.claimNo, props.companyId)
      .then((response: any) => {
        setClaimDetailMaster(response.claimMaster);
        setDiagDataSource(response.claimDiags);
        setDetailsDataSource(response.claimDetails);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getClaimAttachments = () => {
    return props.getAttachments(props.claimNo, props.companyId)
      .then((response: any[]) => {
        setAttachmentList(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const isModalVisibleHandler = () => {
    setIsModalVisible(false);
  };

  const reportIssue = () => {
    setIsModalVisible(true);
  };

  const onPrint = () => {
    window.print();
  };

  return (
    <>
      {props.isFetchingEZ || props.isFetchingAttachments ? (
        <>
         <Skeleton
           active
           paragraph={{ rows: 4 }}
         />
         <br />
         <Skeleton
           active
           avatar
           paragraph={{ rows: 8 }}
         />
         <br />
         <Skeleton
           active
           paragraph={{ rows: 4 }}
         />
        </>
      ) : (
        <div className="row flex-lg-wrap justify-content-center mb-3 ml-0 mt-3 print-claim-info">

          <div className="only-print">
            <div className="d-flex align-items-center" style={{ width: '50%', float: 'left' }}>
              <Image
                src={astranaLogoPlum}
                width={'150px'}
              />
            </div>
          </div>

          <div className="print-center col-print-12">
            <Title level={4}>Claim Detail</Title>
          </div>

          <div className="claim-detail row flex-lg-wrap justify-content-start">
            <div className="col-lg-12 col-md-12 mt-2" />

            <div className="col-lg-12 col-md-12 mt-2 row no-print">
              <div className="btn-wrapper col-lg">
                <Button type="primary" style={{ width: '100%', background: '#1BB294', borderColor: '#1BB294' }} onClick={onPrint}><PrinterOutlined /> Print</Button>
              </div>
              <div className="btn-wrapper col-lg">
                <Button type="primary" style={{ width: '100%', background: '#EE5564', borderColor: '#EE5564' }} onClick={reportIssue}><WarningOutlined /> Claim Issue</Button>
              </div>
            </div>

            <div className="col-lg-12 col-md-12 mt-2 col-print-6">
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Claim No</th>
                    <td>{getDisplayCompanyId(claimDetailMaster.RmiCompanyId)} - {claimDetailMaster.RmiClaimNo}</td>
                  </tr>
                  <tr>
                  <th className="bold">Date Received</th>
                    <td>{claimDetailMaster.RmiDateRecd ? moment(claimDetailMaster.RmiDateRecd).format('MM/DD/YYYY') : null}</td>
                  </tr>
                  <tr>
                  <th className="bold">Auth No</th>
                    <td>{claimDetailMaster.RmiAuthNo}</td>
                  </tr>
                  <tr>
                  <th className="bold">Provider Claim No</th>
                    <td>
                    {claimDetailMaster.RmiProvClaim}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="col-lg-12 col-md-12 mt-2 col-print-6">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Status</th>
                    <td>{claimDetailMaster.RmiStatus}</td>
                  </tr>
                  <tr>
                  <th className="bold">Date Paid</th>
                    <td>{claimDetailMaster.RmiDatePaid ? moment(claimDetailMaster.RmiDatePaid).format('MM/DD/YYYY'): null}</td>
                  </tr>
                  <tr>
                  <th className="bold">Check No</th>
                    <td>{claimDetailMaster.RmiCheckNo}</td>
                  </tr>
                  <tr>
                  <th className="bold">Place of Service</th>
                    <td>
                      {claimDetailMaster.RmiPlaceSvc}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Divider dashed />

            <div className="col-lg-12 col-md-12 col-print-6">
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Member</th>
                    <td>{claimDetailMaster.RmiMembName}</td>
                  </tr>
                  <tr>
                  <th className="bold">Gender</th>
                    <td>{claimDetailMaster.RmiGender}</td>
                  </tr>
                  <tr>
                  <th className="bold">HP Name</th>
                    <td>{claimDetailMaster.RmiHpName}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="col-lg-12 col-md-12 col-print-6">
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Member ID</th>
                    <td>{claimDetailMaster.RmiMembId}</td>
                  </tr>
                  <tr>
                  <th className="bold">DOB</th>
                    <td>{claimDetailMaster.RmiBirth ? moment(claimDetailMaster.RmiBirth).format('MM/DD/YYYY') : null}</td>
                  </tr>
                  <tr>
                  <th className="bold">Opt</th>
                    <td>{claimDetailMaster.RmiOpt}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Divider dashed />

            <div className="col-lg-12 col-md-12 col-print-6">
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Provider Name</th>
                    <td>{claimDetailMaster.RmiRevFullName}</td>
                  </tr>
                </tbody>
              </table>
            </div>


            <div className="col-lg-12 col-md-12 col-print-6">
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Provider ID</th>
                    <td>{claimDetailMaster.RmiProvId}</td>
                  </tr>
                  <tr>
                    <th className="bold">Specialty</th>
                    <td>{claimDetailMaster.RmiSpec}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Divider dashed />

            <div className="col-lg-12 col-md-12 col-print-6">
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Resubmission Code</th>
                    <td>{claimDetailMaster.RmiFrequencyCode}</td>
                  </tr>
                </tbody>
              </table>
            </div>


            <div className="col-lg-12 col-md-12 col-print-6">
              <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr>
                    <th className="bold">Original Ref No</th>
                    <td>{claimDetailMaster.RmiMiscOriginalRef}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Divider dashed />

            <div className="col-lg-24 mb-4 mt-2">
              <Title level={5}>Diagnosis</Title>
              <Divider />
              <Table
                columns={diagColumns}
                dataSource={diagDataSource}
                pagination={false}
                rowKey={'RmiDiagCode'}
                bordered
              />
            </div>

            <div className="col-lg-24 mb-4">
              <Title level={5}>Details</Title>
              <Divider />
              <Table
                columns={procColumns}
                dataSource={[...detailsDataSource, summary]}
                pagination={false}
                rowKey={'RmiProcCode'}
                bordered
              />
            </div>

            <div className="col-lg-24 mb-4">
              <Title level={5}>Memo Line 1:</Title>
              <br/>
              {claimDetailMaster.RmiMemoLine1}
            </div>

            <Divider />

            <div className="col-lg-24 mb-4">
              <Title level={5}>Attachments:</Title>
              <ul>
                {attachmentList.map(attachment => <li>{attachment}</li>)}
              </ul>
            </div>
          </div>

          <ReportIssueModal
            companyId={props.companyId}
            reportIssue={props.reportIssue}
            isReporting={props.isReporting}
            claimNo={props.claimNo}
            isReportIssueModalVisible={isModalVisible}
            handleReportIssueModalClose={isModalVisibleHandler}
            status={props.status}
          />

        </div>
      )}
    </>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingEZ: store.rootReducer.claim.isFetchingEZ,
  isFetchingAttachments: store.rootReducer.claimMember.isFetching,
  isReporting: store.rootReducer.app.isReporting,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getEZ,
    getAttachments,
    reportIssue,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(SearchClaimDetail);